import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiOrder, OrderStatus } from '../../core/api/api.types';
import { ORDER_STATUS_LABELS } from '../../core/api/api.types';
import { OrdersApiService } from '../../core/api/orders-api.service';

type StatusFilter = 'all' | OrderStatus;

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: ORDER_STATUS_LABELS.completed },
  { value: 'completed_externally', label: ORDER_STATUS_LABELS.completed_externally },
  { value: 'referral_sent', label: ORDER_STATUS_LABELS.referral_sent },
  { value: 'closed', label: ORDER_STATUS_LABELS.closed },
  { value: 'cancelled', label: ORDER_STATUS_LABELS.cancelled },
];

@Component({
  selector: 'op-past-orders-page',
  imports: [
    RouterModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DatePipe,
  ],
  templateUrl: './past-orders.page.html',
  styleUrl: './past-orders.page.scss',
})
export class PastOrdersPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly ordersApi = inject(OrdersApiService);

  readonly orders = signal<ApiOrder[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly conversationId = signal<string | null>(null);

  readonly searchQuery = signal('');
  readonly statusFilter = signal<StatusFilter>('all');

  readonly statusFilterOptions = STATUS_FILTER_OPTIONS;

  readonly filteredOrders = computed<ApiOrder[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const sf = this.statusFilter();
    return this.orders().filter((o) => {
      if (sf !== 'all' && o.status !== sf) return false;
      if (!q) return true;
      const haystack = [
        o.order_code,
        o.service_name,
        o.category,
        o.providers?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  });

  readonly completedCount = computed(
    () => this.orders().filter((o) => o.status === 'completed' || o.status === 'completed_externally').length,
  );

  readonly cancelledCount = computed(
    () => this.orders().filter((o) => o.status === 'cancelled').length,
  );

  readonly preferredProvider = computed<string>(() => {
    const counts: Record<string, number> = {};
    for (const o of this.orders()) {
      const name = o.providers?.name;
      if (name) counts[name] = (counts[name] ?? 0) + 1;
    }
    const entries = Object.entries(counts);
    if (!entries.length) return '—';
    return entries.reduce((best, cur) => (cur[1] > best[1] ? cur : best))[0];
  });

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.paramMap;
    const conversationId = params.get('conversationId');
    this.conversationId.set(conversationId);

    if (!conversationId) return;

    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await this.ordersApi.pastOrdersForConversation(conversationId);
      this.orders.set(res.items ?? []);
    } catch (err: any) {
      console.warn('[PastOrders] load failed', err);
      this.error.set('Failed to load past orders.');
    } finally {
      this.loading.set(false);
    }
  }

  statusLabel(status: string): string {
    return (ORDER_STATUS_LABELS as Record<string, string>)[status] ?? status;
  }

  statusTone(status: string): 'green' | 'orange' | 'blue' | 'red' {
    switch (status as OrderStatus) {
      case 'completed':
      case 'completed_externally':
      case 'referral_sent':
        return 'green';
      case 'cancelled':
        return 'orange';
      case 'closed':
        return 'red';
      default:
        return 'blue';
    }
  }

  providerName(order: ApiOrder): string {
    if (order.providers?.name) return order.providers.name;
    if (order.provider_assignment_type === 'unknown') return 'Unknown';
    if (order.provider_assignment_type === 'other_in_notes') return 'Other (in notes)';
    return '—';
  }

  backRoute(): any[] {
    const cid = this.conversationId();
    return cid ? ['/my-cases', cid] : ['/my-cases'];
  }
}